import PropTypes from 'prop-types';
import { useFormContext, Controller } from 'react-hook-form';
import {
    Box,
    TextField,
    InputAdornment,
    Stack,
    Typography
} from '@mui/material';
import InfoOutlineIcon from '@mui/icons-material/InfoOutline';

import { useTheme } from '@emotion/react';

const textFieldColor = "rgba(239, 239, 240, 1)";
const disabledTextFieldColor = "rgba(220, 220, 220, 1)";

const TextFieldComponent = ({
    name,
    placeholder, 
    type,
    required=false, 
    fullWidth=true, 
    contrastTheme=false,
    label="",
    prefix,
    suffix,
    helper,
    inputColor,
    disabled=false,
    sx,
    ...other
}) => {
    const theme = useTheme();

    const { control } = useFormContext();

    return (
        <Controller
            name={name}
            control={control}
            render={({ field, fieldState: { error } }) => (
                <Box sx={{my: 1.5}}>
                    {
                        label
                        ?
                        <Typography
                            fontWeight={400}
                            fontSize={{xs: 16, md: 16, lg: 24}}
                            letterSpacing={0.3}
                            pb={1}
                        >
                            {label}
                        </Typography>
                        :
                        <></>
                    }
                    
                    <TextField 
                        placeholder={placeholder}
                        variant='outlined'
                        value={type === 'number' && field.value === 0 ? '' : field.value}
                        type={type ? type : 'text'}
                        onChange={(event) => {
                            if (type === 'number') {
                                field.onChange(Number(event.target.value));
                            } else {
                                field.onChange(event.target.value);
                            }
                        }}
                        fullWidth={fullWidth}
                        sx={{
                            "& .MuiOutlinedInput-root": {
                                backgroundColor: disabled
                                ?
                                disabledTextFieldColor
                                :
                                contrastTheme ? 'white' : `${textFieldColor}`,
                                borderRadius: '8px',
                                "& .MuiOutlinedInput-notchedOutline": {
                                    borderColor: error 
                                    ? 'rgba(255, 0, 0, 1)' 
                                    : 
                                    disabled
                                    ?
                                    '#666666'
                                    :
                                    contrastTheme ? 'white' : `${textFieldColor}`,
                                },
                                "&.Mui-focused": {
                                    "& .MuiOutlinedInput-notchedOutline": {
                                        borderColor: theme.palette.primary.main,
                                        borderWidth: "1px",
                                    },
                                }
                            },
                            ...sx
                        }}
                        disabled={disabled}
                        error={!!error}
                        helperText={
                            error
                            ?
                            error.message
                            :
                            <>
                                {
                                    helper
                                    ?
                                    <Stack direction='row'>
                                        <InfoOutlineIcon sx={{width: '13px', pt: 3, pr: 5}}/> 
                                        {helper}
                                    </Stack>
                                    :
                                    null
                                }
                            </>
                        }
                        slotProps={{
                            inputLabel: {
                                style: { color: 'rgba(145, 158, 171, 1)' }
                            },
                            formHelperText:{
                                sx: {
                                    color: error ? 'rgba(255, 0, 0, 1)' : '#637381'
                                }
                            },
                            input : {
                                startAdornment: (
                                    prefix
                                    ?
                                    <InputAdornment 
                                        position="start"
                                    >
                                        {prefix}
                                    </InputAdornment>
                                    :
                                    null
                                ),
                                endAdornment : (
                                    suffix
                                    ?
                                    <InputAdornment position="end">
                                        {suffix}
                                    </InputAdornment>
                                    :
                                    null
                                ),
                                style: {
                                    color: inputColor ?? 'rgba(175, 175, 175, 1)',
                                    fontWeight: disabled ? 500 : 600,
                                    fontSize: disabled ? 14 : 16
                                }
                            }
                        }}
                        {...other}
                    />
                </Box>
            )}
        />
    );
}

export default TextFieldComponent;

TextFieldComponent.propTypes = {
    name: PropTypes.string,
    placeholder: PropTypes.string,
    type: PropTypes.string,
    required: PropTypes.bool,
    fullWidth: PropTypes.bool,
    prefix: PropTypes.object,
    suffix: PropTypes.object,
    helper: PropTypes.string,
    inputColor: PropTypes.string,
    label: PropTypes.string,
    sx: PropTypes.object,
    contrastTheme: PropTypes.bool,
    disabled: PropTypes.bool
}